import React, { useEffect, useState } from 'react';
import { Button, Form, Table } from 'react-bootstrap';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line } from 'recharts';
import NavigationBar from '../components/layout/Navbar';
import { notification } from 'antd';
import { useAuth } from '../context/AuthContext';
import { createPrecipitacion, getPrecipitaciones, getCentrosProduccion } from '../services/api';

const obtenerHoy = () => {
  const hoy = new Date();
  const mm = String(hoy.getMonth() + 1).padStart(2, '0');
  const dd = String(hoy.getDate()).padStart(2, '0');
  return `${hoy.getFullYear()}-${mm}-${dd}`;
};

const formatterFecha = (valor) => {
  return valor ? new Date(valor).toLocaleDateString('es-CO') : '';
};

const Precipitacion = () => {
  const { user } = useAuth();
  const [fecha, setFecha] = useState(obtenerHoy());
  const [mmLluvia, setMmLluvia] = useState('');
  const [precipitaciones, setPrecipitaciones] = useState([]);
  const [centros, setCentros] = useState([]);
  const [centroSeleccionado, setCentroSeleccionado] = useState('');
  const [mes, setMes] = useState(new Date().getMonth() + 1);
  const [año, setAño] = useState(new Date().getFullYear());

  useEffect(() => {
    cargarPrecipitaciones();
  }, [mes, año, centroSeleccionado]);

  useEffect(() => {
    if (user?.role === 'Admin') {
      getCentrosProduccion()
        .then((data) => setCentros(data || []))
        .catch((error) => console.error('Error al cargar centros de producción:', error));
    } else if (user?.id_cp) {
      setCentroSeleccionado(user.id_cp);
    }
  }, [user]);

  const cargarPrecipitaciones = async () => {
    try {
      const inicio = `${año}-${String(mes).padStart(2, '0')}-01`;
      const fin = `${año}-${String(mes).padStart(2, '0')}-${new Date(año, mes, 0).getDate()}`;
      const data = await getPrecipitaciones(inicio, fin, centroSeleccionado || null);
      setPrecipitaciones(data || []);
    } catch (error) {
      console.error('Error al obtener precipitaciones:', error);
      notification.error({
        message: 'Error',
        description: 'No se pudieron cargar los datos de precipitación.',
      });
    }
  };

  const diasDelMes = new Date(año, mes, 0).getDate();
  const tablaDias = Array.from({ length: diasDelMes }, (_, index) => {
    const dia = index + 1;
    const fechaDia = `${año}-${String(mes).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
    const registro = precipitaciones.find((item) => item.fecha === fechaDia);
    return {
      dia,
      mm_lluvia: registro ? Number(registro.mm_lluvia) : 0,
      fecha: fechaDia,
    };
  });

  const totalMes = precipitaciones.reduce((acc, item) => acc + Number(item.mm_lluvia || 0), 0);
  const promedioMes = precipitaciones.length ? totalMes / precipitaciones.length : 0;
  const diasRegistrados = precipitaciones.length;
  const registroSeleccionado = precipitaciones.find((item) => item.fecha === fecha);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!fecha) {
      notification.warning({ message: 'Fecha requerida', description: 'Selecciona una fecha.' });
      return;
    }
    if (mmLluvia === '' || isNaN(Number(mmLluvia))) {
      notification.warning({ message: 'Valor inválido', description: 'Ingresa un número válido de precipitación.' });
      return;
    }

    try {
      // Validar que Admin tenga un centro seleccionado
      if (user?.role === 'Admin' && !centroSeleccionado) {
        notification.warning({ message: 'Centro requerido', description: 'Selecciona un centro de producción como administrador.' });
        return;
      }

      const payload = {
        fecha,
        mm_lluvia: Number(mmLluvia),
      };
      if (centroSeleccionado) {
        payload.centro_produccion_id = Number(centroSeleccionado);
      }

      await createPrecipitacion(payload);
      notification.success({ message: 'Guardado', description: 'Precipitación registrada correctamente.' });
      setMmLluvia('');
      const fechaObj = new Date(fecha);
      setMes(fechaObj.getMonth() + 1);
      setAño(fechaObj.getFullYear());
      cargarPrecipitaciones();
    } catch (error) {
      console.error('Error al guardar precipitación:', error);
      notification.error({
        message: 'Fallo',
        description: error.response?.data?.message || 'No se pudo guardar la precipitación.',
      });
    }
  };

  const handleFechaChange = (e) => {
    const nuevaFecha = e.target.value;
    setFecha(nuevaFecha);
    const fechaObj = new Date(nuevaFecha);
    if (!Number.isNaN(fechaObj.getTime())) {
      setMes(fechaObj.getMonth() + 1);
      setAño(fechaObj.getFullYear());
    }
  };

  const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

  return (
    <div className="precipitacion-page">
      <NavigationBar />
      <div className="container-fluid py-4" style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
        <h2 className="mb-1">Precipitación</h2>
        <p className="text-muted mb-4">Registra solo el número de precipitación para la fecha seleccionada. Las estadísticas se calculan automáticamente.</p>

        <div className="row gy-4">
          <div className="col-12 col-lg-3">
            <div className="card p-3 mb-3">
              <h5>Registrar precipitación</h5>
              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label>Fecha</Form.Label>
                  <Form.Control type="date" value={fecha} onChange={handleFechaChange} required />
                </Form.Group>

                {user?.role === 'Admin' && (
                  <Form.Group className="mb-3">
                    <Form.Label>Centro de Producción</Form.Label>
                    <Form.Select value={centroSeleccionado} onChange={(e) => setCentroSeleccionado(e.target.value)}>
                      <option value="">Seleccionar centro</option>
                      {centros.map((centro) => (
                        <option key={centro.id} value={centro.id}>{centro.nombre}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                )}

                <Form.Group className="mb-3">
                  <Form.Label>Precipitación (mm)</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.1"
                    min="0"
                    value={mmLluvia}
                    onChange={(e) => setMmLluvia(e.target.value)}
                    placeholder="Ej. 12.4"
                    required
                  />
                </Form.Group>

                <Button type="submit" variant="primary" className="w-100">
                  Guardar
                </Button>
              </Form>
            </div>
          </div>

          <div className="col-12 col-lg-9">
            <div className="card p-4 mb-4">
              <h5 className="mb-3">Estadísticas del mes</h5>
              <div className="row gy-3 mb-4">
                <div className="col-6 col-lg-3">
                  <div className="bg-light p-3 rounded text-center">
                    <div style={{ fontSize: '0.85rem', color: '#666' }}>Total</div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#0c5ff4' }}>{totalMes.toFixed(2)}</div>
                    <div style={{ fontSize: '0.85rem', color: '#999' }}>mm</div>
                  </div>
                </div>
                <div className="col-6 col-lg-3">
                  <div className="bg-light p-3 rounded text-center">
                    <div style={{ fontSize: '0.85rem', color: '#666' }}>Promedio</div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#28a745' }}>{promedioMes.toFixed(2)}</div>
                    <div style={{ fontSize: '0.85rem', color: '#999' }}>mm</div>
                  </div>
                </div>
                <div className="col-6 col-lg-3">
                  <div className="bg-light p-3 rounded text-center">
                    <div style={{ fontSize: '0.85rem', color: '#666' }}>Días registrados</div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#17a2b8' }}>{diasRegistrados}</div>
                    <div style={{ fontSize: '0.85rem', color: '#999' }}>días</div>
                  </div>
                </div>
                <div className="col-6 col-lg-3">
                  <div className="bg-light p-3 rounded text-center">
                    <div style={{ fontSize: '0.85rem', color: '#666' }}>Hoy</div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#ffc107' }}>{registroSeleccionado ? `${Number(registroSeleccionado.mm_lluvia).toFixed(2)}` : '0.00'}</div>
                    <div style={{ fontSize: '0.85rem', color: '#999' }}>mm</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="card p-4 mb-4">
              <h5 className="mb-3">Gráfica diaria - {new Date(año, mes - 1).toLocaleDateString('es-CO', { month: 'long', year: 'numeric' })}</h5>
              {tablaDias.filter(d => d.mm_lluvia > 0).length > 0 ? (
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart data={tablaDias} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                    <XAxis dataKey="dia" stroke="#999" />
                    <YAxis label={{ value: 'Precipitación (mm)', angle: -90, position: 'insideLeft' }} stroke="#999" />
                    <Tooltip 
                      formatter={(value) => [`${Number(value).toFixed(2)} mm`, 'Lluvia']}
                      labelFormatter={(label) => `Día ${label}`}
                      contentStyle={{ backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '4px' }}
                    />
                    <Legend />
                    <Bar dataKey="mm_lluvia" fill="#4CAF50" radius={[8, 8, 0, 0]} name="mm de lluvia" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center text-muted py-5">
                  <p>No hay datos de precipitación registrados para este mes.</p>
                </div>
              )}
            </div>

            <div className="card p-4">
              <h5 className="mb-3">Detalle de días</h5>
              <div style={{ maxHeight: '450px', overflowY: 'auto' }}>
                <Table striped responsive size="sm">
                  <thead className="table-light sticky-top">
                    <tr>
                      <th>Día</th>
                      <th>Precipitación (mm)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tablaDias.map((row) => (
                      <tr key={row.dia} className={row.mm_lluvia > 0 ? 'table-primary' : 'text-muted'}>
                        <td style={{ fontWeight: '500' }}>{String(row.dia).padStart(2, '0')}</td>
                        <td>{row.mm_lluvia > 0 ? `${Number(row.mm_lluvia).toFixed(2)} mm` : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Precipitacion;
